import { PlusOutlined }              from '@ant-design/icons';
import { MinusCircleOutlined }       from '@ant-design/icons';
import { Button }                    from 'antd';
import type { FormInstance }         from 'antd';
import { Space }                     from 'antd';
import React                         from 'react';
import { Form }                      from 'antd';
import { ApiCollection }             from 'collections/ApiCollection';
import { AppFormComponent }          from 'components/App/AppForm';
import Input                         from 'components/App/IntlInput';
import FormattedMessageWrapper       from 'components/App/FormattedMessageWrapper';
import SourceModel                   from 'models/SourceModel';
import ShowSourceQueryParameterModel from 'models/ShowSourceQueryParameterModel';
import handleApiError                from 'utils/handleApiError';
import handleFormError               from 'utils/handleFormError';
import ShowModel                     from 'models/ShowModel';
import { messageSuccessIntl }        from 'utils/messageIntl';

interface SourceQueryPropertiesFormProps {
  onSuccess?: () => void;
  setLoading?: (loading: boolean) => void;
  show: ShowModel;
  source: SourceModel;
}

class ShowSourceQueryParametersForm extends AppFormComponent<SourceQueryPropertiesFormProps> {
  public collSourceQueryProperty = new ApiCollection(ShowSourceQueryParameterModel);

  public static defaultProps = {
    setLoading: () => {},
  };

  public formRef: React.RefObject<FormInstance> = React.createRef<FormInstance>();

  public componentDidMount = async () => {
    const {
      show,
      source,
      setLoading,
    } = this.props;

    if (setLoading) {
      setLoading(true);
    }

    await this.collSourceQueryProperty
        .setFilter('show', show.id)
        .setFilter('source', source.id)
        .list();

    const initialValues: {
      sourceQueryProperties: Record<number, { id: id, name: string, value: string }>,
    } = {
      sourceQueryProperties: [],
    };

    this.collSourceQueryProperty.forEach((sqp: ShowSourceQueryParameterModel, index) => {
      if (index) {
        initialValues.sourceQueryProperties[index] = {
          id: sqp.id,
          name: sqp.sourceQueryPropertyName,
          value: sqp.value,
        };
      }
    });

    this.formRef.current?.setFieldsValue(initialValues);

    if (setLoading) {
      setLoading(false);
    }

    this.setState({
      loading: false,
    });
  };

  public render() {
    return (
        <Form
            className="antd-form-custom-style"
            ref={this.formRef}
        >
          <Form.List name="sourceQueryProperties">
            {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                      <Space
                          align="baseline"
                          key={key}
                          style={{
                            display: 'flex',
                            marginBottom: 8
                          }}
                      >
                        <Form.Item
                            {...restField}
                            name={[name, 'id']}
                            style={{ display: 'none' }}
                        >
                          <Input disabled={true} placeholder="Id" />
                        </Form.Item>
                        <Form.Item
                            {...restField}
                            name={[name, 'name']}
                            rules={[{
                              message: <FormattedMessageWrapper id="Information manquante" />,
                              required: true,
                            }]}
                        >
                          <Input placeholder="Clé" />
                        </Form.Item>
                        <Form.Item
                            {...restField}
                            name={[name, 'value']}
                            rules={[{
                              message: <FormattedMessageWrapper id="Information manquante" />,
                              required: true,
                            }]}
                        >
                          <Input placeholder="Valeur" />
                        </Form.Item>
                        <MinusCircleOutlined
                            onClick={async () => {
                              const value = this.formRef.current?.getFieldValue(['sourceQueryProperties', name, 'id']);
                              const found = this.collSourceQueryProperty.findBy('id', value) as ShowSourceQueryParameterModel;
                              if (found) {
                                try {
                                  await found.destroy();
                                } catch (error) {
                                  handleApiError(error);
                                  return;
                                }
                              }
                              remove(name);
                            }}
                        />
                      </Space>
                  ))}
                  <Form.Item>
                    <Button
                        block
                        icon={<PlusOutlined />}
                        onClick={() => add()}
                        type="dashed"
                        style={{
                          alignItems: 'center',
                          display: 'flex',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                    >
                      <FormattedMessageWrapper id="Ajouter un paramètre" />
                    </Button>
                  </Form.Item>
                </>
            )}
          </Form.List>
        </Form>
    );
  }

  public submit = async () => {
    const values = await this.formRef.current?.validateFields();

    const {
      show,
      source,
      onSuccess,
    } = this.props;

    try {
      await Promise.all(values.sourceQueryProperties.map(async (datum: {
        id?: number;
        name: string;
        value: string;
      }) => {
        return await (new ShowSourceQueryParameterModel({
          id: datum.id,
        })).patch({
          ...datum,
          show: show.iri,
          source: source.iri,
        });
      }));

      messageSuccessIntl("Enregistrement réussi !");

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      handleFormError(error, this.formRef.current);
    }
  };
}

export default ShowSourceQueryParametersForm;
